<script lang="ts" setup>
import { ref } from 'vue'
import { usePwa } from '../composable'

const { needsRefresh, offlineReady, updateServiceWorker } = usePwa()
const isUpdateDismissed = ref(false)
const isOfflineDismissed = ref(false)

const handleUpdate = (): void => {
  updateServiceWorker()
}

const handleDismissUpdate = (): void => {
  isUpdateDismissed.value = true
}

const handleDismissOffline = (): void => {
  isOfflineDismissed.value = true
}
</script>

<template>
  <div>
    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="needsRefresh && !isUpdateDismissed"
        class="fixed bottom-4 left-4 max-w-sm border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800 z-50"
        data-testid="pwa-update-notification"
      >
        <div class="p-4">
          <div class="flex items-start justify-between mb-3">
            <div class="flex-1">
              <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">
                Update Available
              </h3>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                A new version is available. Reload to update.
              </p>
            </div>
            <button
              aria-label="Dismiss"
              class="ml-3 w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
              data-testid="pwa-dismiss-update-button"
              @click="handleDismissUpdate"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <div class="flex gap-2">
            <button
              class="flex-1 px-4 py-2 text-sm border border-slate-900 bg-slate-900 text-white hover:bg-slate-800 hover:border-slate-800 dark:border-slate-100 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200 dark:hover:border-slate-200 transition-all duration-150"
              data-testid="pwa-update-button"
              @click="handleUpdate"
            >
              Reload
            </button>
            <button
              class="px-4 py-2 text-sm border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:border-slate-600 transition-all duration-150"
              @click="handleDismissUpdate"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </transition>

    <transition
      enter-active-class="transition-all duration-200 ease-out"
      enter-from-class="opacity-0 translate-y-2"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 translate-y-2"
    >
      <div
        v-if="offlineReady && !isOfflineDismissed && !needsRefresh"
        class="fixed bottom-4 left-4 max-w-sm border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800 z-50"
        data-testid="pwa-offline-ready"
      >
        <div class="p-4">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100 mb-1">
                Ready to work offline
              </h3>
              <p class="text-sm text-slate-600 dark:text-slate-400">
                App is ready to work offline.
              </p>
            </div>
            <button
              aria-label="Dismiss"
              class="ml-3 w-6 h-6 flex items-center justify-center text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors duration-150"
              data-testid="pwa-dismiss-offline-button"
              @click="handleDismissOffline"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 18L18 6M6 6l12 12" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
